import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Wallet,
  Gamepad2,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Activity,
  DollarSign,
  Trophy,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Card } from '@/components/ui/Card';
import { Badge, getStatusVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { dashboardApi } from '@/lib/adminApi';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Dashboard() {
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('7d');

  // Fetch real stats from API - refetch when timeRange changes
  const { data: apiStats } = useQuery({
    queryKey: ['dashboardStats', timeRange],
    queryFn: async () => {
      try {
        const response = await dashboardApi.getStats(timeRange);
        return response.data.data;
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        return null;
      }
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  // Transform API data or use zeros as fallback
  const stats = apiStats ? {
    totalUsers: apiStats.users?.total || 0,
    newUsersToday: apiStats.users?.newToday || 0,
    userGrowth: apiStats.users?.growth || 0,
    totalDeposits: apiStats.transactions?.totalDeposits || 0,
    depositGrowth: apiStats.transactions?.depositGrowth || 0,
    totalWithdrawals: apiStats.transactions?.totalWithdrawals || 0,
    withdrawalGrowth: apiStats.transactions?.withdrawalGrowth || 0,
    totalRevenue: apiStats.revenue?.netRevenue || 0,
    revenueGrowth: apiStats.revenue?.revenueGrowth || 0,
    activeGames: apiStats.games?.activeLudoGames || 0,
    completedGamesToday: apiStats.games?.gamesToday || 0,
    pendingWithdrawals: apiStats.transactions?.pendingWithdrawals || 0,
    pendingKYC: apiStats.kyc?.pending || 0,
    onlineUsers: apiStats.users?.active || 0,
    openTickets: 0,
  } : {
    totalUsers: 0,
    newUsersToday: 0,
    userGrowth: 0,
    totalDeposits: 0,
    depositGrowth: 0,
    totalWithdrawals: 0,
    withdrawalGrowth: 0,
    totalRevenue: 0,
    revenueGrowth: 0,
    activeGames: 0,
    completedGamesToday: 0,
    pendingWithdrawals: 0,
    pendingKYC: 0,
    onlineUsers: 0,
    openTickets: 0,
  };

  // Chart data - empty until API provides this data
  const revenueData: { date: string; revenue: number; deposits: number; withdrawals: number }[] = [];

  // Game distribution - empty until API provides this data
  const gameDistribution: { name: string; value: number; color: string }[] = [];

  // Recent activity - empty until API provides this data
  const recentActivity: { id: number; type: string; user: string; amount: number; time: string; status: string }[] = [];

  // Top winners - empty until API provides this data
  const topWinners: { rank: number; name: string; wins: number; earnings: number }[] = [];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </motion.div>

      {/* Key Stats Grid */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={formatNumber(stats.totalUsers)}
          change={stats.userGrowth}
          icon={Users}
          color="purple"
          subtitle={`+${stats.newUsersToday} today`}
        />
        <StatCard
          title="Total Deposits"
          value={formatCurrency(stats.totalDeposits)}
          change={stats.depositGrowth}
          icon={Wallet}
          color="green"
        />
        <StatCard
          title="Total Withdrawals"
          value={formatCurrency(stats.totalWithdrawals)}
          change={stats.withdrawalGrowth}
          icon={DollarSign}
          color="amber"
        />
        <StatCard
          title="Net Revenue"
          value={formatCurrency(stats.totalRevenue)}
          change={stats.revenueGrowth}
          icon={TrendingUp}
          color="blue"
        />
      </motion.div>

      {/* Secondary Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MiniStatCard title="Active Games" value={stats.activeGames} icon={Gamepad2} />
        <MiniStatCard title="Pending Withdrawals" value={stats.pendingWithdrawals} icon={Clock} warning />
        <MiniStatCard title="Pending KYC" value={stats.pendingKYC} icon={AlertTriangle} warning />
        <MiniStatCard title="Open Tickets" value={stats.openTickets} icon={Activity} />
      </motion.div>

      {/* Charts Row */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <Card variant="glass" className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">Revenue Overview</h3>
              <p className="text-sm text-slate-400">Daily revenue, deposits & withdrawals</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-violet-500" />
                <span className="text-sm text-slate-400">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm text-slate-400">Deposits</span>
              </div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="depositsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(value) => `₹${value / 1000}K`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '12px',
                    padding: '12px',
                  }}
                  labelStyle={{ color: '#94A3B8' }}
                  formatter={(value: number) => [formatCurrency(value), '']}
                />
                <Area
                  type="monotone"
                  dataKey="deposits"
                  stroke="#10B981"
                  strokeWidth={2}
                  fill="url(#depositsGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Game Distribution */}
        <Card variant="glass" className="p-6">
          <h3 className="text-lg font-semibold mb-6">Game Distribution</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gameDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {gameDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '12px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-3 mt-4">
            {gameDistribution.map((game) => (
              <div key={game.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: game.color }} />
                  <span className="text-sm text-slate-400">{game.name}</span>
                </div>
                <span className="text-sm font-medium">{game.value}%</span>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Bottom Row */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card variant="glass" className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Recent Activity</h3>
            <Button variant="ghost" size="sm">
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        </Card>

        {/* Top Winners */}
        <Card variant="glass" className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Top Winners Today</h3>
            <Trophy className="w-5 h-5 text-amber-400" />
          </div>
          <div className="space-y-4">
            {topWinners.map((winner) => (
              <div key={winner.rank} className="flex items-center gap-4">
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                    winner.rank === 1
                      ? 'bg-amber-500/20 text-amber-400'
                      : winner.rank === 2
                      ? 'bg-slate-400/20 text-slate-400'
                      : winner.rank === 3
                      ? 'bg-orange-500/20 text-orange-400'
                      : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {winner.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{winner.name}</p>
                  <p className="text-sm text-slate-400">{winner.wins} wins</p>
                </div>
                <span className="text-sm font-semibold text-emerald-400">
                  {formatCurrency(winner.earnings)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  change: number;
  icon: React.ElementType;
  color: 'purple' | 'green' | 'amber' | 'blue' | 'red';
  subtitle?: string;
}

function StatCard({ title, value, change, icon: Icon, color, subtitle }: StatCardProps) {
  const isPositive = change >= 0;
  const colorClasses = {
    purple: 'from-violet-500/20 to-violet-600/20 border-violet-500/30',
    green: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-600/20 border-amber-500/30',
    blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
    red: 'from-red-500/20 to-red-600/20 border-red-500/30',
  };
  const iconColors = {
    purple: 'text-violet-400 bg-violet-500/20',
    green: 'text-emerald-400 bg-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/20',
    red: 'text-red-400 bg-red-500/20',
  };

  return (
    <div className={`glass-card-hover p-6 bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400 font-medium">{title}</p>
          <h3 className="text-2xl font-bold mt-2">{value}</h3>
          <div className="flex items-center gap-2 mt-2">
            <span
              className={`flex items-center text-sm font-medium ${
                isPositive ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {isPositive ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              {Math.abs(change)}%
            </span>
            {subtitle && <span className="text-sm text-slate-500">{subtitle}</span>}
          </div>
        </div>
        <div className={`p-3 rounded-xl ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

// Mini Stat Card Component
function MiniStatCard({
  title,
  value,
  icon: Icon,
  warning,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  warning?: boolean;
}) {
  return (
    <div className="glass-card p-4 flex items-center gap-4">
      <div
        className={`p-2 rounded-lg ${
          warning ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-800 text-slate-400'
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className={`text-xl font-bold ${warning && value > 0 ? 'text-amber-400' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

// Activity Item Component
interface Activity {
  id: number;
  type: string;
  user: string;
  amount: number;
  time: string;
  status: string;
}

function ActivityItem({ activity }: { activity: Activity }) {
  const getIcon = () => {
    switch (activity.type) {
      case 'deposit':
        return <ArrowDownRight className="w-4 h-4" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4" />;
      case 'game_win':
        return <Trophy className="w-4 h-4" />;
      case 'kyc':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getIconBg = () => {
    switch (activity.type) {
      case 'deposit':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'withdrawal':
        return 'bg-amber-500/20 text-amber-400';
      case 'game_win':
        return 'bg-violet-500/20 text-violet-400';
      case 'kyc':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-slate-800 text-slate-400';
    }
  };

  return (
    <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-800/30 transition-colors">
      <div className={`p-2 rounded-lg ${getIconBg()}`}>{getIcon()}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{activity.user}</p>
        <p className="text-sm text-slate-400 capitalize">{activity.type.replace('_', ' ')}</p>
      </div>
      {activity.amount > 0 && (
        <span
          className={`font-semibold ${
            activity.type === 'deposit' || activity.type === 'game_win'
              ? 'text-emerald-400'
              : 'text-white'
          }`}
        >
          {activity.type === 'deposit' || activity.type === 'game_win' ? '+' : '-'}
          {formatCurrency(activity.amount)}
        </span>
      )}
      <Badge variant={getStatusVariant(activity.status)}>{activity.status}</Badge>
      <span className="text-sm text-slate-500">{activity.time}</span>
    </div>
  );
}
